<template>

<!--
	The leaderboard: three editorial tabs (Daily / Streaks / All-time). Each tab
	is its own scope on the server (api.request('leaderboard', { scope })), so a
	tab change refetches. Rows are { rank, account:{name,avatar}, value }; the
	value's column label changes per tab (Score / Streak / Points). The signed-in
	player's own row is highlighted. Chrome (Topbar / Foot) wraps the table.
-->

<div class="leaderboard">

	<app-topbar v-on:signin="$emit('signin')" />

	<main class="leaderboard-body">

		<div class="leaderboard-card">

			<h1 class="leaderboard-title">Leaderboard</h1>

			<div class="leaderboard-tabs" role="tablist">
				<button
					v-for="tab in tabs"
					:key="tab.scope"
					type="button"
					class="leaderboard-tab"
					:class="{ 'is-current': tab.scope === scope }"
					role="tab"
					:aria-selected="tab.scope === scope ? 'true' : 'false'"
					v-on:click="select(tab.scope)"
				>{{ tab.label }}</button>
			</div>

			<div class="leaderboard-head">
				<span class="leaderboard-head-label">Player</span>
				<span class="leaderboard-head-label">{{ valueLabel }}</span>
			</div>

			<div v-if="loading" class="leaderboard-state">Loading…</div>

			<div v-else-if="!rows.length" class="lb-empty">
				<app-mark :size="42" />
				<div class="lb-empty-head">No scores yet</div>
				<p class="lb-empty-sub">{{ emptyText }}</p>
				<router-link to="/" class="lb-empty-cta">Play today’s puzzle</router-link>
			</div>

			<div v-else class="leaderboard-rows">
				<div
					v-for="(row, index) in rows"
					:key="row.rank + '-' + index"
					class="leaderboard-row"
					:class="{ 'is-me': isMe(row) }"
				>
					<span class="leaderboard-rank">{{ row.rank }}</span>
					<span class="leaderboard-avatar" :style="avatarStyle(row)">
						<span v-if="!hasAvatar(row)" class="leaderboard-initial">{{ initial(row) }}</span>
					</span>
					<span class="leaderboard-name">{{ rowName(row) }}</span>
					<span class="leaderboard-value">{{ row.value }}</span>
				</div>
			</div>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

import api from '../service/api'

export default {

	name: 'Leaderboard',

	data: function() {

		return {
			tabs: [
				{ scope: 'daily', label: 'Daily', value: 'Score' },
				{ scope: 'streaks', label: 'Streaks', value: 'Streak' },
				{ scope: 'alltime', label: 'All-time', value: 'Points' }
			],
			scope: 'daily',
			rows: [],
			loading: false
		}

	},

	computed: {

		account: function() {

			return this.$store.getters['account/account']

		},

		valueLabel: function() {

			var tab = this.tabs.find(function(t) { return t.scope === this.scope }, this)

			return tab ? tab.value : 'Score'

		},

		// a friendly line under the empty-state heading, tuned to the active tab
		emptyText: function() {

			if (this.scope === 'streaks') return 'Solve the puzzle day after day to build a streak. The longest runs land here.'

			if (this.scope === 'alltime') return 'Points add up across every puzzle you play. Keep solving to climb the all-time board.'

			return 'No one has solved today’s puzzle yet. Crack it and you’ll top the board.'

		}

	},

	created: function() {

		this.fetch()

	},

	methods: {

		select: function(scope) {

			if (scope === this.scope) return

			this.scope = scope
			this.fetch()

		},

		fetch: function() {

			var self = this
			var scope = this.scope

			this.loading = true

			return api.request('leaderboard', { scope: scope }).then(function(json) {

				// a stale tab's response (the player flicked on) must not overwrite
				// the current one
				if (scope !== self.scope) return

				self.loading = false
				self.rows = (json && json.rows) || []

			})

		},

		rowName: function(row) {

			return (row.account && row.account.name) || 'Player'

		},

		hasAvatar: function(row) {

			return !!(row.account && row.account.avatar)

		},

		avatarStyle: function(row) {

			return this.hasAvatar(row) ? { backgroundImage: 'url(' + row.account.avatar + ')' } : {}

		},

		initial: function(row) {

			var name = this.rowName(row)

			return name ? name.charAt(0).toUpperCase() : '?'

		},

		isMe: function(row) {

			if (!this.account) return false

			// the server may flag the player's own row, else match on id/name
			if (row.me) return true

			var mine = row.account && row.account.id

			return mine != null && mine === this.account.id

		}

	}

}

</script>

<style scoped>

.leaderboard {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

.leaderboard-body {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 20px 16px;
}

.leaderboard-card {
	width: 100%;
	max-width: 480px;
}

.leaderboard-title {
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0 0 16px;
	color: var(--ink);
}

.leaderboard-tabs {
	display: flex;
	gap: 26px;
	border-bottom: 1px solid var(--hairline);
	margin-bottom: 6px;
}

.leaderboard-tab {
	background: none;
	border: none;
	padding: 0 0 11px;
	margin-bottom: -1px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	cursor: pointer;
	border-bottom: 2px solid transparent;
}

.leaderboard-tab:hover {
	color: var(--ink);
}

.leaderboard-tab.is-current {
	color: var(--ink);
	font-weight: 600;
	border-bottom-color: var(--ink);
}

.leaderboard-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 2px 10px;
}

.leaderboard-head-label {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.1em;
	color: var(--muted);
	text-transform: uppercase;
}

.leaderboard-state {
	padding: 28px 2px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-align: center;
}

/* empty state: mark + heading + tuned blurb + CTA back to today's puzzle */
.lb-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 40px 20px 36px;
}

.lb-empty >>> .mark,
.lb-empty >>> svg {
	opacity: 0.9;
}

.lb-empty-head {
	margin-top: 16px;
	font-family: var(--font-display);
	font-size: 20px;
	font-weight: 600;
	color: var(--ink);
}

.lb-empty-sub {
	margin: 7px auto 0;
	max-width: 300px;
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--muted-2);
}

.lb-empty-cta {
	margin-top: 18px;
	display: inline-flex;
	align-items: center;
	height: 42px;
	padding: 0 22px;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
}

.lb-empty-cta:hover {
	background: var(--accent);
}

.leaderboard-rows {
	display: flex;
	flex-direction: column;
}

.leaderboard-row {
	position: relative;
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 12px;
	border-top: 1px solid #efe9dc;
	border-radius: 6px;
}

.leaderboard-row.is-me {
	background: #eef1f6;
}

.leaderboard-row.is-me::before {
	content: '';
	position: absolute;
	left: 0;
	top: 6px;
	bottom: 6px;
	width: 3px;
	background: var(--accent);
	border-radius: 3px;
}

.leaderboard-rank {
	font-family: var(--font-mono);
	font-size: 14px;
	color: var(--muted);
	width: 20px;
	flex: none;
}

.leaderboard-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background-color: #ece5d7;
	background-size: cover;
	background-position: center;
	border: 1px solid #e0d8c8;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
}

.leaderboard-initial {
	font-family: var(--font-display);
	font-size: 15px;
	color: var(--muted-2);
}

.leaderboard-name {
	flex: 1;
	font-family: var(--font-body);
	font-size: 15px;
	color: var(--ink);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.leaderboard-value {
	font-family: var(--font-mono);
	font-size: 16px;
	color: var(--ink);
	flex: none;
}

</style>
